import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useForm, Controller } from 'react-hook-form'

import { fetchCurrentUserSilent } from '../../actions/authActions'
import {
  verifySMSCode,
  verifyPhoneNumber,
} from '../../actions/phoneNumberActions'

import { Label, InputGroup, Hint, TextField, ErrorLabel } from '../form'

import Button from '../display/Button'
import Spinner from '../display/Spinner'
import Overlay from '../display/Overlay'

const Wrapper = styled.div`
  position: relative;
  margin: 15px 0;
  padding: 30px;
  background-color: ${({ theme }) => theme.colors.grey};
`

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const InputFlexButtons = styled.div`
  display: flex;
  flex: 1;
  margin: 8px -8px;

  button {
    flex: 1;
    margin: 0 8px;
  }
`

const normalizeCode = (value) => {
  if (!value) return value
  const onlyNums = value.replace(/[^\d]/g, '')
  return onlyNums.slice(0, 4)
}

const PhoneVerificationForm = ({ phoneNumber, roundedInputs }) => {
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitted },
  } = useForm({ defaultValues: { code: '' } })

  const onSubmit = async ({ code }) => {
    try {
      await dispatch(verifySMSCode({ phone_number: phoneNumber, code }))
      dispatch(fetchCurrentUserSilent())
    } catch (e) {
      setError('code', {
        type: 'server',
        message: 'Invalid SMS verification code.',
      })
    }
  }

  const submit = handleSubmit(onSubmit)

  const handleResend = () => {
    reset({ code: '' })
    dispatch(verifyPhoneNumber(phoneNumber))
  }

  const handleKeyDownEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  const codeError = errors.code?.message

  return (
    <Wrapper>
      <InputGroup noMargin>
        <div style={{ marginBottom: 30 }}>
          <Hint>
            We’ve sent you a verification code via SMS. Enter it here to verify
            your mobile number.
          </Hint>
        </div>
        <FlexWrapper>
          <Label htmlFor="code">
            Verification code
            <Controller
              control={control}
              name="code"
              rules={{ required: 'This field is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id="code"
                  onChange={(e) =>
                    field.onChange(normalizeCode(e.target.value))
                  }
                  placeholder="----"
                  onKeyDown={handleKeyDownEnter}
                  rounded={roundedInputs}
                />
              )}
            />
          </Label>
          <InputFlexButtons>
            <Button
              type="button"
              onClick={submit}
              rounded={roundedInputs}
              uppercase={false}
            >
              Verify
            </Button>
            <Button
              type="button"
              onClick={handleResend}
              rounded={roundedInputs}
              uppercase={false}
              ghost
            >
              Resend
            </Button>
          </InputFlexButtons>
        </FlexWrapper>
        {codeError && isSubmitted && (
          <ErrorLabel htmlFor="code">{codeError}</ErrorLabel>
        )}
      </InputGroup>

      {(auth.verifyingSMSCode || auth.verifyingPhoneNumber) && (
        <Overlay>
          <Spinner />
        </Overlay>
      )}
    </Wrapper>
  )
}

export default PhoneVerificationForm
